import { resolve, singleton } from 'aurelia';
import { NotificationPreferences } from '../models';
import { AnalyticsService } from './analytics-service';
import { HttpClient } from './http-client';

// Owns the signed-in user's account state (Auth0 via the server BFF), the account dropdown,
// browser time-zone sync, and the email notification preferences shown in the Profile modal.
// Distinct from AdminService, which handles the separate single-admin login.
@singleton()
export class AccountService {
  private readonly http: HttpClient = resolve(HttpClient);
  private readonly analytics: AnalyticsService = resolve(AnalyticsService);

  isAuthenticated: boolean = false;
  userLoginConfigured: boolean = false;
  userEmail: string | null = null;
  userDisplayName: string | null = null;
  userTimeZone: string = 'UTC';
  userTier: string = 'free';
  accountMenuOpen: boolean = false;

  // Email notification preferences (daily digest + per-item master switches) and the Profile
  // settings modal that edits them. Defaults keep existing users opted in until loaded.
  notificationPreferences: NotificationPreferences = {
    digestOptIn: true,
    notifySavedSearches: true,
    notifyAvailability: true,
    savedSearchFrequency: 'daily'
  };
  profileOpen: boolean = false;
  savingPreferences: boolean = false;
  profileError: string | null = null;

  // First-run email opt-in gate. `notificationsPrompted` mirrors the server flag; when a
  // signed-in user has not been prompted yet, the SPA shows the one-time consent modal
  // (`emailOptInOpen`). Defaults keep existing sessions from ever showing it.
  notificationsPrompted: boolean = true;
  emailOptInOpen: boolean = false;
  savingOptIn: boolean = false;
  optInError: string | null = null;

  // First-visit welcome overlay. Shown once to anonymous visitors to introduce the product
  // and nudge them to create a free account. Dismissal is remembered in localStorage so it
  // never reappears for the same browser; signed-in visitors never see it.
  welcomeOpen: boolean = false;

  // Loads the signed-in user's account state (Auth0 via the server BFF). Always returns a
  // clean answer for anonymous visitors, so it is safe to call unconditionally on startup.
  async checkAccount(): Promise<void> {
    try {
      const response = await this.http.get('/api/account/me');
      if (!response.ok) return;
      const data = await response.json();
      this.isAuthenticated = data.isAuthenticated === true;
      this.userLoginConfigured = data.loginConfigured === true;
      this.userEmail = data.email ?? null;
      this.userDisplayName = data.displayName ?? null;
      this.userTimeZone = typeof data.timeZone === 'string' ? data.timeZone : 'UTC';
      this.userTier = typeof data.tier === 'string' ? data.tier : 'free';
      this.notificationsPrompted = data.notificationsPrompted !== false;
      const prefs = data.notificationPreferences;
      if (prefs && typeof prefs === 'object') {
        this.notificationPreferences = {
          digestOptIn: prefs.digestOptIn !== false,
          notifySavedSearches: prefs.notifySavedSearches !== false,
          notifyAvailability: prefs.notifyAvailability !== false,
          savedSearchFrequency:
            prefs.savedSearchFrequency === 'every-update' ? 'every-update' : 'daily'
        };
      }
    } catch {
      // Non-fatal: treat as signed out.
      this.isAuthenticated = false;
    }
  }

  // Starts the Auth0 login by navigating to the server challenge endpoint, returning to
  // the current page afterward. This is a full-page redirect (the BFF pattern), not XHR.
  signIn(): void {
    // Every sign-up/sign-in entry point funnels through here, so this is the one place that
    // can count redirect starts. Sent with keepalive so it survives the navigation below.
    this.analytics.track('signin_started');
    const returnUrl = window.location.pathname + window.location.search;
    window.location.href = `/api/account/login?returnUrl=${encodeURIComponent(returnUrl)}`;
  }

  // Signs the user out via the server, which also clears the Auth0 session.
  signOut(): void {
    this.accountMenuOpen = false;
    const returnUrl = window.location.pathname + window.location.search;
    window.location.href = `/api/account/logout?returnUrl=${encodeURIComponent(returnUrl)}`;
  }

  toggleAccountMenu(): void {
    this.accountMenuOpen = !this.accountMenuOpen;
  }

  closeAccountMenu(): void {
    this.accountMenuOpen = false;
  }

  // Persists the browser's IANA time zone to the user's account when it differs from what
  // the server already has. Best-effort: failures are ignored so they never block the UI.
  async syncTimeZone(): Promise<void> {
    if (!this.isAuthenticated) return;
    let browserZone = 'UTC';
    try {
      browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return;
    }
    if (!browserZone || browserZone === this.userTimeZone) return;
    try {
      const response = await this.http.sendWithCsrf('PUT', '/api/account/timezone', {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ timeZone: browserZone })
      });
      if (response.ok) {
        this.userTimeZone = browserZone;
      }
    } catch {
      // Non-fatal.
    }
  }

  openProfile(): void {
    if (!this.isAuthenticated) return;
    this.profileError = null;
    this.accountMenuOpen = false;
    this.profileOpen = true;
  }

  closeProfile(): void {
    this.profileOpen = false;
    this.profileError = null;
  }

  // Persists the current notification preferences via PUT /api/account/notification-preferences.
  async saveNotificationPreferences(): Promise<void> {
    if (this.savingPreferences) return;
    this.savingPreferences = true;
    this.profileError = null;
    try {
      const response = await this.http.sendWithCsrf('PUT', '/api/account/notification-preferences', {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          digestOptIn: this.notificationPreferences.digestOptIn,
          notifySavedSearches: this.notificationPreferences.notifySavedSearches,
          notifyAvailability: this.notificationPreferences.notifyAvailability,
          savedSearchFrequency: this.notificationPreferences.savedSearchFrequency
        })
      });
      if (!response.ok) {
        this.profileError = 'Could not save your preferences. Please try again.';
        return;
      }
      this.profileOpen = false;
    } catch {
      this.profileError = 'Could not save your preferences. Please try again.';
    } finally {
      this.savingPreferences = false;
    }
  }

  // Opens the one-time email opt-in prompt when the signed-in user has not answered it yet.
  // Called during startup after the account state is loaded. No-op for anonymous visitors or
  // users who have already been prompted, so it is safe to call unconditionally.
  openEmailOptInIfNeeded(): void {
    if (this.isAuthenticated && !this.notificationsPrompted) {
      this.optInError = null;
      this.emailOptInOpen = true;
    }
  }

  // Records the user's answer to the first-run opt-in. When `optIn` is false, all email
  // channels are turned off; when true, the current (default-on) preferences are kept. Either
  // way the server marks the account as prompted so the modal never shows again.
  async submitEmailOptIn(optIn: boolean): Promise<void> {
    if (this.savingOptIn) return;
    this.savingOptIn = true;
    this.optInError = null;
    const prefs: NotificationPreferences = optIn
      ? { ...this.notificationPreferences }
      : {
          digestOptIn: false,
          notifySavedSearches: false,
          notifyAvailability: false,
          savedSearchFrequency: this.notificationPreferences.savedSearchFrequency
        };
    try {
      const response = await this.http.sendWithCsrf('PUT', '/api/account/notification-optin', {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(prefs)
      });
      if (!response.ok) {
        this.optInError = 'Could not save your choice. Please try again.';
        return;
      }
      this.notificationPreferences = prefs;
      this.notificationsPrompted = true;
      this.emailOptInOpen = false;
    } catch {
      this.optInError = 'Could not save your choice. Please try again.';
    } finally {
      this.savingOptIn = false;
    }
  }

  // localStorage key that records the visitor already saw (and dismissed) the welcome overlay.
  private static readonly welcomeSeenKey = 'tc.welcomeSeen';

  // Opens the first-visit welcome overlay when appropriate: only for anonymous visitors who
  // have not dismissed it before on this browser. Called during startup after account state is
  // loaded, so it is safe to call unconditionally (no-op for signed-in or returning visitors).
  openWelcomeIfNeeded(): void {
    if (this.isAuthenticated) return;
    let seen = false;
    try {
      seen = window.localStorage.getItem(AccountService.welcomeSeenKey) === '1';
    } catch {
      // Private-mode or storage disabled: fall through and show the overlay this session.
    }
    if (!seen) {
      this.welcomeOpen = true;
      this.analytics.track('welcome_shown');
    }
  }

  // Dismisses the welcome overlay and remembers the choice so it never shows again on this
  // browser. Used by both the "maybe later" link and the sign-up button. `abandoned` is false
  // when sign-up follows, so a conversion is never also counted as a drop-off.
  dismissWelcome(abandoned: boolean = true): void {
    this.welcomeOpen = false;
    if (abandoned) {
      this.analytics.track('welcome_dismissed');
    }
    try {
      window.localStorage.setItem(AccountService.welcomeSeenKey, '1');
    } catch {
      // Non-fatal: worst case the overlay shows again next visit.
    }
  }

  // Marks the welcome overlay as seen, then starts the Auth0 sign-up/sign-in redirect.
  signUpFromWelcome(): void {
    this.dismissWelcome(false);
    this.signIn();
  }

  // Contextual sign-up nudge. Surfaced at moments of intent (e.g. an anonymous visitor tapping
  // a release reminder bell) to convert interest into a free account. `signupNudgeMessage`
  // carries the context-specific line shown in the small prompt.
  signupNudgeOpen: boolean = false;
  signupNudgeMessage: string = '';

  // Opens the contextual sign-up nudge with a context-specific message. No-op for signed-in
  // users, so callers can invoke it unconditionally from a shared handler.
  promptSignup(message: string): void {
    if (this.isAuthenticated) return;
    this.signupNudgeMessage = message;
    this.signupNudgeOpen = true;
    this.analytics.track('nudge_shown');
  }

  dismissSignupNudge(): void {
    this.signupNudgeOpen = false;
    this.analytics.track('nudge_dismissed');
  }

  // Closes the nudge and starts the Auth0 sign-up/sign-in redirect.
  signUpFromNudge(): void {
    this.signupNudgeOpen = false;
    this.signIn();
  }
}
